import { ACTIVITY_ACTION_TYPES } from '../actions/types';
import { getActivitySliceKey } from '../constants/activity';
import {
  applyLocallyReadStateToActivityItems,
  collectReadActivityIdsFromTab,
  countExplicitUnreadInRawItems,
  getActivityNotificationStableId,
  reconcileActivitySliceUnreadCount,
} from '../utils/activityUtil';

const emptyTab = () => ({
  items: [],
  pagination: null,
  unreadCount: 0,
  loading: false,
  error: null,
  // Whether we have attempted to fetch this tab variant at least once.
  // Used by the Activity page to avoid refetching on tab switches.
  fetched: false,
});

const initialState = {
  byTab: {},
};

const activityReducer = (state = initialState, action) => {
  switch (action.type) {
    case ACTIVITY_ACTION_TYPES.TAB_REQUEST: {
      const { tabId, grouped, archived, page = 1 } = action.payload;
      const sliceKey = getActivitySliceKey({ grouped, archived });
      const prevByTab = state.byTab[tabId] || { byGrouped: {} };
      const prev = prevByTab.byGrouped[sliceKey] || emptyTab();
      return {
        ...state,
        byTab: {
          ...state.byTab,
          [tabId]: {
            ...prevByTab,
            byGrouped: {
              ...prevByTab.byGrouped,
              [sliceKey]: {
                ...prev,
                // Optimistically set the requested page so the UI can update immediately.
                pagination: {
                  ...(prev.pagination || {}),
                  page,
                },
                loading: true,
                error: null,
              },
            },
          },
        },
      };
    }

    case ACTIVITY_ACTION_TYPES.TAB_SUCCESS: {
      const { tabId, grouped, archived, items, pagination, unreadCount, append, page } =
        action.payload;
      const sliceKey = getActivitySliceKey({ grouped, archived });
      const prevByTab = state.byTab[tabId] || { byGrouped: {} };
      const prev = prevByTab.byGrouped[sliceKey] || emptyTab();
      const readIds = collectReadActivityIdsFromTab(prevByTab.byGrouped);
      let nextItems = applyLocallyReadStateToActivityItems(readIds, items);
      if (append && page > 1) {
        const seen = new Set(
          prev.items.map((x) => getActivityNotificationStableId(x)).filter(Boolean)
        );
        nextItems = [
          ...prev.items,
          ...nextItems.filter((x) => {
            const sid = getActivityNotificationStableId(x);
            return sid && !seen.has(sid);
          }),
        ];
      }

      const nextSlice = {
        ...prev,
        items: nextItems,
        pagination,
        unreadCount,
        loading: false,
        error: null,
        fetched: true,
      };

      return {
        ...state,
        byTab: {
          ...state.byTab,
          [tabId]: {
            ...prevByTab,
            byGrouped: {
              ...prevByTab.byGrouped,
              [sliceKey]: {
                ...nextSlice,
                unreadCount: reconcileActivitySliceUnreadCount(nextSlice),
              },
            },
          },
        },
      };
    }

    case ACTIVITY_ACTION_TYPES.RESET_UNREAD_COUNTS: {
      const nextByTab = { ...state.byTab };
      for (const tabId of Object.keys(nextByTab)) {
        const tabState = nextByTab[tabId];
        if (!tabState?.byGrouped) continue;
        const nextGrouped = { ...tabState.byGrouped };
        let tabChanged = false;
        for (const [groupedKey, slice] of Object.entries(nextGrouped)) {
          if (!slice?.items) continue;
          const synced = countExplicitUnreadInRawItems(slice.items);
          if (slice.unreadCount === synced) continue;
          tabChanged = true;
          nextGrouped[groupedKey] = { ...slice, unreadCount: synced };
        }
        if (tabChanged) {
          nextByTab[tabId] = { ...tabState, byGrouped: nextGrouped };
        }
      }
      return { ...state, byTab: nextByTab };
    }

    case ACTIVITY_ACTION_TYPES.ITEM_VIEWED: {
      const { tabId, stableId } = action.payload;
      const prevByTab = state.byTab[tabId] || { byGrouped: {} };
      if (!prevByTab.byGrouped || !stableId) {
        return state;
      }

      const markRowRead = (row) => {
        if (getActivityNotificationStableId(row) !== stableId) return row;
        return {
          ...row,
          isRead: true,
          isViewed: true,
          read: true,
          viewed: true,
          is_read: true,
          is_viewed: true,
        };
      };

      let matched = false;
      const nextGrouped = { ...prevByTab.byGrouped };
      for (const [groupedKey, slice] of Object.entries(nextGrouped)) {
        if (!slice?.items?.length) continue;
        let sliceMatched = false;
        const nextItems = slice.items.map((row) => {
          if (getActivityNotificationStableId(row) !== stableId) return row;
          sliceMatched = true;
          matched = true;
          return markRowRead(row);
        });
        if (!sliceMatched) continue;
        const nextSlice = { ...slice, items: nextItems };
        nextGrouped[groupedKey] = {
          ...nextSlice,
          unreadCount: reconcileActivitySliceUnreadCount(nextSlice),
        };
      }

      if (!matched) {
        return state;
      }

      return {
        ...state,
        byTab: {
          ...state.byTab,
          [tabId]: {
            ...prevByTab,
            byGrouped: nextGrouped,
          },
        },
      };
    }

    case ACTIVITY_ACTION_TYPES.TAB_FAILURE: {
      const { tabId, grouped, archived, error } = action.payload;
      const sliceKey = getActivitySliceKey({ grouped, archived });
      const prevByTab = state.byTab[tabId] || { byGrouped: {} };
      const prev = prevByTab.byGrouped[sliceKey] || emptyTab();
      return {
        ...state,
        byTab: {
          ...state.byTab,
          [tabId]: {
            ...prevByTab,
            byGrouped: {
              ...prevByTab.byGrouped,
              [sliceKey]: {
                ...prev,
                loading: false,
                error,
                // Mark as fetched so we don't refetch on tab switch.
                fetched: true,
              },
            },
          },
        },
      };
    }

    default:
      return state;
  }
};

export default activityReducer;
